import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DOCUMENT_LANGUAGES,
  DocumentCraftingDocumentListParams,
  DocumentCraftingLanguage,
  DocumentCraftingListScope,
  DocumentCraftingListSort,
  DocumentCraftingType,
} from "../../../Models/documentCrafting";

// The whole list state lives in the URL, so a filtered list can be shared and
// survives "Back" from a document. Defaults are left out of the URL.
// `Core/useTableParams` cannot be used: it rebuilds the query string from its
// own five keys and drops every filter on each page, sort or search change.

export const DOCUMENT_LIST_PAGE_SIZES = [20, 50, 100] as const;
export const DEFAULT_DOCUMENT_LIST_PAGE_SIZE = 20;
export const DEFAULT_DOCUMENT_LIST_SORT: DocumentCraftingListSort =
  "last_revision";

export type SortOrder = "asc" | "desc";

export const DOCUMENT_LIST_SCOPES: DocumentCraftingListScope[] = [
  "all",
  "mine",
  "shared",
];

export const DOCUMENT_LIST_SORTS: DocumentCraftingListSort[] = [
  "last_revision",
  "created",
  "title",
  "type",
  "revisions",
];

/** Direction a column starts with: newest / most first, names A–Z. */
export const DEFAULT_SORT_ORDER: Record<DocumentCraftingListSort, SortOrder> = {
  last_revision: "desc",
  created: "desc",
  title: "asc",
  type: "asc",
  revisions: "desc",
};

/** URL keys; they match the API's query names. */
export const LIST_URL_KEYS = {
  scope: "scope",
  q: "q",
  type: "type",
  label: "label",
  personalLabel: "personal_label",
  language: "language",
  createdBy: "created_by",
  from: "from",
  to: "to",
  hasDraft: "has_draft",
  sort: "sort",
  order: "order",
  page: "page",
  pageSize: "page_size",
} as const;

type UrlKey = (typeof LIST_URL_KEYS)[keyof typeof LIST_URL_KEYS];

/** null or an empty list removes the key; an array writes one entry per item. */
export type ListUrlPatch = Partial<Record<UrlKey, string | string[] | null>>;

/** The keys "Clear filters" resets (search included; scope, sort and page
 * size stay). */
const FILTER_KEYS: UrlKey[] = [
  LIST_URL_KEYS.q,
  LIST_URL_KEYS.type,
  LIST_URL_KEYS.label,
  LIST_URL_KEYS.personalLabel,
  LIST_URL_KEYS.language,
  LIST_URL_KEYS.createdBy,
  LIST_URL_KEYS.from,
  LIST_URL_KEYS.to,
  LIST_URL_KEYS.hasDraft,
];

export interface DocumentListState {
  scope: DocumentCraftingListScope;
  q: string;
  types: DocumentCraftingType[];
  labels: number[];
  personalLabels: number[];
  language: DocumentCraftingLanguage | null;
  createdBy: number | null;
  /** YYYY-MM-DD (latest saved revision), or null. */
  from: string | null;
  to: string | null;
  hasDraft: boolean;
  sort: DocumentCraftingListSort;
  order: SortOrder;
  page: number;
  pageSize: number;
}

const TYPE_VALUES = Object.keys(DocumentCraftingType)
  .map(Number)
  .filter((value) => !Number.isNaN(value));

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const positiveInt = (value: string | null) => {
  if (value === null || !/^\d+$/.test(value)) return null;
  const number = Number(value);
  return number > 0 ? number : null;
};

// Repeated keys (`type=1&type=2`); invalid and duplicate entries are dropped.
const idList = (params: URLSearchParams, key: string) => {
  const ids: number[] = [];
  params.getAll(key).forEach((raw) => {
    const id = positiveInt(raw);
    if (id !== null && ids.indexOf(id) === -1) ids.push(id);
  });
  return ids;
};

const oneOf = <T extends string>(
  value: string | null,
  allowed: readonly T[],
): T | null =>
  value !== null && (allowed as readonly string[]).indexOf(value) !== -1
    ? (value as T)
    : null;

// A real calendar day ("2026-02-30" from a hand-edited link would be a 400).
const dateValue = (value: string | null) => {
  if (!value || !DATE_PATTERN.test(value)) return null;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) &&
    date.toISOString().slice(0, 10) === value
    ? value
    : null;
};

/** Reads the list state from the URL; unknown or invalid values fall back to
 * the defaults, so a hand-edited link never breaks the page. */
export const parseDocumentListParams = (
  params: URLSearchParams,
): DocumentListState => {
  const pageSize = positiveInt(params.get(LIST_URL_KEYS.pageSize));
  const from = dateValue(params.get(LIST_URL_KEYS.from));
  const to = dateValue(params.get(LIST_URL_KEYS.to));
  // A reversed range is a 400 (`invalid_query`); keep only "from" then.
  // YYYY-MM-DD strings compare like dates.
  const sort =
    oneOf(params.get(LIST_URL_KEYS.sort), DOCUMENT_LIST_SORTS) ??
    DEFAULT_DOCUMENT_LIST_SORT;
  return {
    scope: oneOf(params.get(LIST_URL_KEYS.scope), DOCUMENT_LIST_SCOPES) ?? "all",
    q: (params.get(LIST_URL_KEYS.q) ?? "").trim(),
    types: idList(params, LIST_URL_KEYS.type).filter(
      (value) => TYPE_VALUES.indexOf(value) !== -1,
    ) as DocumentCraftingType[],
    labels: idList(params, LIST_URL_KEYS.label),
    personalLabels: idList(params, LIST_URL_KEYS.personalLabel),
    language: oneOf(params.get(LIST_URL_KEYS.language), DOCUMENT_LANGUAGES),
    createdBy: positiveInt(params.get(LIST_URL_KEYS.createdBy)),
    from,
    to: from && to && from > to ? null : to,
    hasDraft: params.get(LIST_URL_KEYS.hasDraft) === "true",
    sort,
    order:
      oneOf(params.get(LIST_URL_KEYS.order), ["asc", "desc"] as const) ??
      DEFAULT_SORT_ORDER[sort],
    page: positiveInt(params.get(LIST_URL_KEYS.page)) ?? 1,
    pageSize:
      pageSize !== null &&
      (DOCUMENT_LIST_PAGE_SIZES as readonly number[]).indexOf(pageSize) !== -1
        ? pageSize
        : DEFAULT_DOCUMENT_LIST_PAGE_SIZE,
  };
};

/** The `GET /documents/` query of a list state (empty filters are dropped by
 * the Api layer). */
export const toDocumentListQuery = (
  state: DocumentListState,
): DocumentCraftingDocumentListParams => ({
  scope: state.scope,
  q: state.q || undefined,
  type: state.types.length ? state.types : undefined,
  label: state.labels.length ? state.labels : undefined,
  personal_label: state.personalLabels.length ? state.personalLabels : undefined,
  language: state.language ?? undefined,
  created_by: state.createdBy ?? undefined,
  from: state.from ?? undefined,
  to: state.to ?? undefined,
  has_draft: state.hasDraft || undefined,
  sort: state.sort,
  order: state.order,
  page: state.page,
  page_size: state.pageSize,
});

/** Number of set filters, search and scope not counted. */
export const countActiveFilters = (state: DocumentListState) =>
  [
    state.types.length > 0,
    state.labels.length > 0 || state.personalLabels.length > 0,
    state.language !== null,
    state.createdBy !== null,
    state.from !== null || state.to !== null,
    state.hasDraft,
  ].filter(Boolean).length;

/**
 * Applies `patch` to a copy of `previous` and keeps every other key, unlike
 * `useTableParams`. `resetPage` drops `page` (back to page 1), which every
 * change except paging wants.
 */
export const mergeSearchParams = (
  previous: URLSearchParams,
  patch: ListUrlPatch,
  resetPage = true,
) => {
  const next = new URLSearchParams(previous.toString());
  (Object.keys(patch) as UrlKey[]).forEach((key) => {
    const value = patch[key];
    next.delete(key);
    if (Array.isArray(value)) value.forEach((item) => next.append(key, item));
    else if (value) next.set(key, value);
  });
  if (resetPage) next.delete(LIST_URL_KEYS.page);
  return next;
};

/** Sort after a click on a column header: the active column flips its
 * direction, another column starts with its natural direction. */
export const nextSort = (
  state: Pick<DocumentListState, "sort" | "order">,
  column: DocumentCraftingListSort,
): { sort: DocumentCraftingListSort; order: SortOrder } =>
  column === state.sort
    ? { sort: column, order: state.order === "asc" ? "desc" : "asc" }
    : { sort: column, order: DEFAULT_SORT_ORDER[column] };

const ids = (values: number[]) => values.map(String);

/** List state of the document overview, kept in the URL query string. */
export const useDocumentListParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // react-router's functional update reads the params of the render that
  // created the setter, so a debounced search would undo a filter changed in
  // the meantime, and two updates in one event would overwrite each other.
  // Updates therefore start from the newest params, kept in a ref that is
  // written in the update itself and synced after every navigation.
  const latest = useRef(searchParams);
  useEffect(() => {
    latest.current = searchParams;
  }, [searchParams]);

  const update = useCallback(
    (patch: ListUrlPatch, resetPage = true) => {
      const next = mergeSearchParams(latest.current, patch, resetPage);
      if (next.toString() === latest.current.toString()) return;
      latest.current = next;
      setSearchParams(next, { replace: true });
    },
    [setSearchParams],
  );

  const state = useMemo(
    () => parseDocumentListParams(searchParams),
    [searchParams],
  );
  const query = useMemo(() => toDocumentListQuery(state), [state]);
  const activeFilterCount = countActiveFilters(state);

  const actions = useMemo(
    () => ({
      setScope: (scope: DocumentCraftingListScope) =>
        update({ [LIST_URL_KEYS.scope]: scope === "all" ? null : scope }),
      setSearch: (q: string) => update({ [LIST_URL_KEYS.q]: q.trim() || null }),
      setTypes: (types: DocumentCraftingType[]) =>
        update({ [LIST_URL_KEYS.type]: ids(types) }),
      setLabels: (projectLabelIds: number[], personalLabelIds: number[]) =>
        update({
          [LIST_URL_KEYS.label]: ids(projectLabelIds),
          [LIST_URL_KEYS.personalLabel]: ids(personalLabelIds),
        }),
      setLanguage: (language: DocumentCraftingLanguage | null) =>
        update({ [LIST_URL_KEYS.language]: language }),
      setCreatedBy: (userId: number | null) =>
        update({
          [LIST_URL_KEYS.createdBy]: userId === null ? null : String(userId),
        }),
      // A date that reverses the range clears the other end, so the date the
      // user just entered wins.
      setFrom: (date: string | null) => {
        const from = dateValue(date);
        const { to } = parseDocumentListParams(latest.current);
        update({
          [LIST_URL_KEYS.from]: from,
          ...(from && to && from > to ? { [LIST_URL_KEYS.to]: null } : {}),
        });
      },
      setTo: (date: string | null) => {
        const to = dateValue(date);
        const { from } = parseDocumentListParams(latest.current);
        update({
          [LIST_URL_KEYS.to]: to,
          ...(from && to && from > to ? { [LIST_URL_KEYS.from]: null } : {}),
        });
      },
      setHasDraft: (hasDraft: boolean) =>
        update({ [LIST_URL_KEYS.hasDraft]: hasDraft ? "true" : null }),
      /** Header click: toggles the active column, starts others naturally. */
      toggleSort: (column: DocumentCraftingListSort) => {
        const current = parseDocumentListParams(latest.current);
        const sort = nextSort(current, column);
        update({
          [LIST_URL_KEYS.sort]:
            sort.sort === DEFAULT_DOCUMENT_LIST_SORT ? null : sort.sort,
          [LIST_URL_KEYS.order]:
            sort.order === DEFAULT_SORT_ORDER[sort.sort] ? null : sort.order,
        });
      },
      setSort: (sort: DocumentCraftingListSort, order: SortOrder) =>
        update({
          [LIST_URL_KEYS.sort]: sort === DEFAULT_DOCUMENT_LIST_SORT ? null : sort,
          [LIST_URL_KEYS.order]:
            order === DEFAULT_SORT_ORDER[sort] ? null : order,
        }),
      setPage: (page: number) =>
        update({ [LIST_URL_KEYS.page]: page > 1 ? String(page) : null }, false),
      setPageSize: (pageSize: number) =>
        update({
          [LIST_URL_KEYS.pageSize]:
            pageSize === DEFAULT_DOCUMENT_LIST_PAGE_SIZE
              ? null
              : String(pageSize),
        }),
      /** Search and filters; scope, sort and page size stay. */
      clearFilters: () => {
        const patch: ListUrlPatch = {};
        FILTER_KEYS.forEach((key) => {
          patch[key] = null;
        });
        update(patch);
      },
    }),
    [update],
  );

  return {
    state,
    query,
    activeFilterCount,
    /** Search or any filter is set (the "no results" empty state). */
    isFiltered: activeFilterCount > 0 || state.q !== "",
    ...actions,
  };
};

export type DocumentListParams = ReturnType<typeof useDocumentListParams>;
